import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { dashboardApi, orgApi, jobsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Wallet,
  Users as UsersIcon,
  Settings2,
  ChevronDown,
  Send,
  Trophy,
  Info,
  ExternalLink,
  Briefcase,
  Building2,
  Calendar as CalendarIcon,
  RefreshCw,
} from 'lucide-react';

const MANAGER_ROLES = new Set(['admin', 'team_lead', 'delivery_manager']);

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
];

const HEALTH_TONE = {
  achieved: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    barClass: 'bg-emerald-500',
    icon: CheckCircle2,
    label: 'Target achieved',
  },
  on_track: {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-700',
    barClass: 'bg-teal-500',
    icon: TrendingUp,
    label: 'On track',
  },
  at_risk: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    barClass: 'bg-amber-500',
    icon: AlertTriangle,
    label: 'At risk',
  },
  off_track: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-800',
    barClass: 'bg-rose-500',
    icon: AlertTriangle,
    label: 'Off track',
  },
  no_target: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-700',
    barClass: 'bg-slate-400',
    icon: Target,
    label: 'No target set',
  },
};

const formatINR = (n) => {
  if (!n || n <= 0) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

// Tiny circular initial chip — mirrors the team-roster avatars but lighter weight
// since the joined-candidates dialog can show 50+ rows.
const UserAvatarInline = ({ name }) => {
  const initial = (name || '?').trim().slice(0, 1).toUpperCase();
  // Deterministic colour so the same recruiter always gets the same shade in the list.
  const palette = ['bg-sky-100 text-sky-700', 'bg-emerald-100 text-emerald-700',
                   'bg-amber-100 text-amber-700', 'bg-violet-100 text-violet-700',
                   'bg-rose-100 text-rose-700', 'bg-teal-100 text-teal-700'];
  const hash = (name || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const cls = palette[hash % palette.length];
  return (
    <span
      className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold ${cls}`}
    >
      {initial}
    </span>
  );
};

/**
 * Closure Health Report — closures count vs aggregated monthly target,
 * scoped to whoever the caller is allowed to see (Recruiter = self,
 * TL = direct reports, DM = whole subtree, Admin = entire org).
 *
 * The time selector switches the *closures count* window only; the target
 * remains the aggregated monthly target (per product spec — does not pro-rate).
 */
const ClosureHealthReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = MANAGER_ROLES.has(user?.role);
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // `loadError` is non-null when the latest fetch timed out or errored out
  // hard (network drop, 5xx, Cloudflare 520, etc.). Surfacing it instead of
  // an infinite spinner lets the user retry without a full page reload.
  const [loadError, setLoadError] = useState(null);

  // ----- Set-target dialog state (managers only) -----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orgGoal, setOrgGoal] = useState(''); // '' = clear
  const [overridePersonal, setOverridePersonal] = useState(false);
  const [savingTarget, setSavingTarget] = useState(false);
  const [autoNudge, setAutoNudge] = useState({
    enabled: false,
    threshold_pct: 50,
    schedule_label: 'Every Friday at 5:00 PM IST',
  });
  const [autoNudgeSaving, setAutoNudgeSaving] = useState(false);
  const [closureOfWeek, setClosureOfWeek] = useState({
    enabled: false,
    schedule_label: 'Every Monday at 9:00 AM IST',
  });
  const [closureOfWeekSaving, setClosureOfWeekSaving] = useState(false);

  // ----- Laggards drill-down state (managers only) -----
  const [laggardsOpen, setLaggardsOpen] = useState(false);
  const [laggards, setLaggards] = useState([]);
  const [laggardsLoading, setLaggardsLoading] = useState(false);
  const [nudgingId, setNudgingId] = useState(null);

  // ----- Joined-candidates drill-down (clicking the headline numbers) -----
  const [joinedOpen, setJoinedOpen] = useState(false);
  const [joinedItems, setJoinedItems] = useState([]);
  const [joinedTotal, setJoinedTotal] = useState(0);
  const [joinedCtcTotal, setJoinedCtcTotal] = useState(0);
  const [joinedLoading, setJoinedLoading] = useState(false);

  // ----- Lens (join_basis) — same precedence chain as the Offer-vs-Joining card -----
  //   1. user's localStorage override (set by flipping the OvJ pill toggle anywhere)
  //   2. org default fetched from /api/organizations/me
  //   3. 'joining_date' fallback (Closure Health was hard-coded to this before)
  // Closure Health doesn't show its own toggle — it inherits the lens so finance
  // & ops see the same numbers in both cards.
  const [joinBasis, setJoinBasis] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('ovj-join-basis') || null;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem('ovj-join-basis')) return;
    let cancelled = false;
    orgApi.getMine().then((res) => {
      const orgDefault = res.data?.default_join_basis;
      if (!cancelled && (orgDefault === 'stage_event' || orgDefault === 'joining_date')) {
        setJoinBasis(orgDefault);
      }
    }).catch(() => { /* silent — backend will fall back to org default itself */ });
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async (p) => {
    setLoading(true);
    setLoadError(null);
    // 30s client-side timeout — Cloudflare itself cuts the wire at ~100s,
    // but well-behaved pages should fail fast and give the user a retry
    // affordance instead of an indefinite spinner.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await dashboardApi.getClosureHealth(p, joinBasis, { signal: controller.signal });
      setData(res.data);
    } catch (e) {
      setData(null);
      // Axios surfaces aborted requests as either `CanceledError` (name) or
      // `ERR_CANCELED` (code). Treat both as timeouts in the UI copy.
      const aborted = e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
      setLoadError(aborted ? 'timeout' : 'error');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [joinBasis]);

  useEffect(() => {
    load(period);
  }, [period, load]);

  const openTargetDialog = useCallback(async () => {
    setOverridePersonal(false);
    setSavingTarget(false);
    // Pre-fill with the current org-wide goal so the manager can see what's set.
    try {
      const res = await orgApi.getMine();
      const cur = res.data?.monthly_closure_goal;
      setOrgGoal(cur && cur > 0 ? String(cur) : '');
    } catch (e) {
      setOrgGoal('');
    }
    // Pre-fill auto-nudge state too — managers can flip both in one place.
    try {
      const res = await dashboardApi.getAutoNudge();
      setAutoNudge({
        enabled: !!res.data?.enabled,
        threshold_pct: res.data?.threshold_pct || 50,
        schedule_label: res.data?.schedule_label || 'Every Friday at 5:00 PM IST',
      });
    } catch (e) {
      // Non-fatal — just keep the defaults
    }
    try {
      const res = await dashboardApi.getClosureOfWeek();
      setClosureOfWeek({
        enabled: !!res.data?.enabled,
        schedule_label: res.data?.schedule_label || 'Every Monday at 9:00 AM IST',
      });
    } catch (e) {
      // Non-fatal
    }
    setDialogOpen(true);
  }, []);

  const handleToggleAutoNudge = useCallback(
    async (next) => {
      setAutoNudgeSaving(true);
      try {
        await dashboardApi.setAutoNudge(next, autoNudge.threshold_pct);
        setAutoNudge((s) => ({ ...s, enabled: next }));
        toast.success(
          next
            ? `Auto-nudge ON — ${autoNudge.schedule_label}`
            : 'Auto-nudge turned off',
        );
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to update auto-nudge');
      } finally {
        setAutoNudgeSaving(false);
      }
    },
    [autoNudge.threshold_pct, autoNudge.schedule_label],
  );

  const handleToggleClosureOfWeek = useCallback(
    async (next) => {
      setClosureOfWeekSaving(true);
      try {
        await dashboardApi.setClosureOfWeek(next);
        setClosureOfWeek((s) => ({ ...s, enabled: next }));
        toast.success(
          next
            ? `Closure-of-the-week ON — ${closureOfWeek.schedule_label}`
            : 'Closure-of-the-week turned off',
        );
      } catch (err) {
        toast.error(
          err?.response?.data?.detail || 'Failed to update Closure of the week',
        );
      } finally {
        setClosureOfWeekSaving(false);
      }
    },
    [closureOfWeek.schedule_label],
  );

  const handleSaveTarget = useCallback(async () => {
    const trimmed = String(orgGoal || '').trim();
    let goalNum = null;
    if (trimmed !== '') {
      const parsed = parseInt(trimmed, 10);
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 500) {
        toast.error('Target must be a number between 0 and 500');
        return;
      }
      goalNum = parsed > 0 ? parsed : null;
    }
    setSavingTarget(true);
    try {
      await jobsApi.setOrgGoal(goalNum, overridePersonal);
      toast.success(
        goalNum
          ? `Org-wide monthly target set to ${goalNum}${
              overridePersonal ? ' (cleared personal overrides)' : ''
            }`
          : 'Org-wide monthly target cleared',
      );
      setDialogOpen(false);
      // Refetch closure-health so the tile reflects the new target immediately.
      load(period);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update target');
    } finally {
      setSavingTarget(false);
    }
  }, [orgGoal, overridePersonal, period, load]);

  const fetchLaggards = useCallback(async () => {
    setLaggardsLoading(true);
    try {
      const res = await dashboardApi.getClosureLaggards(period, 5);
      setLaggards(res.data?.items || []);
    } catch (err) {
      setLaggards([]);
      toast.error(err?.response?.data?.detail || 'Failed to load laggards');
    } finally {
      setLaggardsLoading(false);
    }
  }, [period]);

  const toggleLaggards = useCallback(() => {
    setLaggardsOpen((prev) => {
      const next = !prev;
      if (next) fetchLaggards();
      return next;
    });
  }, [fetchLaggards]);

  // Refetch laggards when period changes IF the panel is already open.
  useEffect(() => {
    if (laggardsOpen) fetchLaggards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // ----- Joined-candidates dialog: fetch + open -----
  const fetchJoinedCandidates = useCallback(async () => {
    setJoinedLoading(true);
    try {
      const res = await dashboardApi.getClosureJoinedCandidates(period, 500, joinBasis);
      setJoinedItems(res.data?.items || []);
      setJoinedTotal(res.data?.total || 0);
      setJoinedCtcTotal(res.data?.candidate_ctc_total || 0);
    } catch (err) {
      setJoinedItems([]);
      setJoinedTotal(0);
      setJoinedCtcTotal(0);
      toast.error(err?.response?.data?.detail || 'Failed to load joined candidates');
    } finally {
      setJoinedLoading(false);
    }
  }, [period, joinBasis]);

  const openJoinedDialog = useCallback(() => {
    setJoinedOpen(true);
    fetchJoinedCandidates();
  }, [fetchJoinedCandidates]);

  // Refetch joined-candidates if the user switches period while the dialog is open
  useEffect(() => {
    if (joinedOpen) fetchJoinedCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, joinBasis]);

  const handleNudge = useCallback(
    async (targetUserId, targetName) => {
      setNudgingId(targetUserId);
      try {
        const res = await dashboardApi.nudgeRecruiter(targetUserId, period);
        const slackOk = res.data?.slack_notified;
        const slackConfigured = res.data?.slack_configured;
        if (slackOk) {
          toast.success(`Nudged ${targetName} on Slack + in-app`);
        } else if (slackConfigured) {
          toast.warning(
            `Nudged ${targetName} in-app. Slack post failed — check the org webhook.`,
            {
              action: {
                label: 'Open settings',
                onClick: () => navigate('/settings'),
              },
            },
          );
        } else {
          toast.success(`Nudged ${targetName} in-app`, {
            description: 'Connect Slack to also celebrate in your channel.',
            action: {
              label: 'Connect Slack',
              onClick: () => navigate('/settings?focus=slack'),
            },
          });
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to send nudge');
      } finally {
        setNudgingId(null);
      }
    },
    [period, navigate],
  );

  const tone = HEALTH_TONE[data?.health || 'no_target'] || HEALTH_TONE.no_target;
  const ToneIcon = tone.icon;
  const pct = Math.min(100, Math.max(0, data?.progress_pct || 0));

  // Click handler for the headline closures number / CTC total. Now opens the
  // joined-candidates drill-down dialog (managers + recruiters alike see the
  // exact rows that built the number). The recruiter-laggards drill stays
  // available on its own toggle below the progress bar.
  const handleHeadlineClick = () => {
    openJoinedDialog();
  };

  // Reusable info-icon tooltip — keeps the JSX below short.
  const InfoTip = ({ id, title, children }) => (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); } }}
            role="button"
            tabIndex={0}
            aria-label={`What does ${title} mean?`}
            data-testid={`closure-health-info-${id}`}
            className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400 cursor-help"
          >
            <Info className="w-3.5 h-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[320px] text-xs leading-relaxed">
          <p className="font-semibold mb-1 text-[11px]">{title}</p>
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Card
      className={`border ${tone.border} ${tone.bg}`}
      data-testid="closure-health-report"
    >
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-9 h-9 rounded-lg bg-white ${tone.text} flex items-center justify-center`}
          >
            <ToneIcon className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold text-slate-900">
              Closure Health Report
            </CardTitle>
            <p className="text-xs text-slate-500">
              {data?.period_label || '—'} · scoped to your team
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-1 bg-white/70 border border-slate-200 rounded-lg p-1"
            data-testid="closure-health-period-selector"
          >
            {PERIODS.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? 'default' : 'ghost'}
                className={`h-7 px-3 text-xs font-medium ${
                  period === p.value
                    ? 'bg-slate-900 text-white hover:bg-slate-800'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setPeriod(p.value)}
                data-testid={`closure-health-period-${p.value}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          {isManager && (
            <Button
              size="sm"
              variant="outline"
              className="h-9 gap-1.5 text-xs font-medium bg-white"
              onClick={openTargetDialog}
              data-testid="closure-health-set-target-btn"
              title="Set org-wide monthly closure target"
            >
              <Settings2 className="w-3.5 h-3.5" />
              Set target
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-slate-500 text-sm" data-testid="closure-health-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading closure health…
          </div>
        ) : loadError ? (
          // 30s timeout (`loadError === 'timeout'`) or hard error (5xx /
          // Cloudflare 520). Surface a retry affordance instead of leaving
          // the user staring at a stale empty card.
          <div
            className="flex flex-col items-start gap-3 py-6 px-1 border border-amber-200 bg-amber-50/60 rounded-md"
            data-testid="closure-health-error"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">
                  {loadError === 'timeout'
                    ? 'Took too long to load (>30s).'
                    : 'Couldn’t load closure health.'}
                </p>
                <p className="text-xs text-amber-800/80 mt-0.5">
                  {loadError === 'timeout'
                    ? 'The server is slow or unreachable. Please retry in a moment.'
                    : 'The server returned an error. Please retry — if it persists, contact your admin.'}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => load(period)}
              className="border-amber-300 text-amber-900 hover:bg-amber-100"
              data-testid="closure-health-retry-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Retry
            </Button>
          </div>
        ) : (
          <>
            {/* Headline numbers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1">
                  Closures
                  <InfoTip id="closures" title="Closures · How it's calculated">
                    <p>
                      Count of applications whose join falls inside the selected window.
                      The lens depends on your org's <code className="bg-slate-200 px-1 rounded mx-0.5">default_join_basis</code> setting:
                    </p>
                    <ul className="mt-1 list-disc pl-4 space-y-0.5">
                      <li><strong>By joining date</strong> — uses the candidate's contractual <code className="bg-slate-200 px-0.5 rounded">joining_date</code>.
                        Best for headcount &amp; revenue reporting.</li>
                      <li><strong>By stage event</strong> — uses when the recruiter marked the candidate as Joined.
                        Best for funnel velocity.</li>
                    </ul>
                    <p className="mt-1">
                      Currently active lens: <strong className="text-white">{data?.join_basis === 'stage_event' ? 'By stage event' : 'By joining date'}</strong>.
                      Admins can change the default in Settings → Analytics defaults.
                    </p>
                    <p className="mt-1 text-slate-300">
                      Scope: Recruiter sees their own; TL sees direct reports; DM sees the whole
                      subtree; Admin sees the entire org.
                    </p>
                  </InfoTip>
                </p>
                <button
                  type="button"
                  onClick={handleHeadlineClick}
                  className={`text-3xl font-bold ${tone.text} text-left hover:underline focus:outline-none focus:underline cursor-pointer`}
                  data-testid="closure-health-count"
                  title="Click to see the list of joined candidates"
                >
                  {data?.closures_count ?? 0}
                  <span className="text-base text-slate-400 font-normal">
                    {' '}
                    / {data?.monthly_target ?? 0}
                  </span>
                </button>
                <p className="text-xs text-slate-500 mt-0.5">
                  {period === 'month'
                    ? 'this month vs monthly target'
                    : `${PERIODS.find((p) => p.value === period)?.label} vs monthly target`}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1">
                  <Wallet className="w-3 h-3" /> Candidate CTC
                  <InfoTip id="ctc" title="Candidate CTC · How it's calculated">
                    <p>
                      Sum of <code className="bg-slate-200 px-1 rounded">offered_ctc</code> for every
                      application whose <strong>JOINED</strong> stage event falls inside the
                      selected window. The date used is the JOINED stage transition (when the card
                      was moved to Joined), <em>not</em> the document's last-updated time. Edits
                      made later do not change which window a record belongs to.
                    </p>
                    <p className="mt-1 text-slate-300">
                      Fallback for legacy rows: <code>expected_ctc</code> → <code>current_ctc</code>.
                      Values above ₹50 Cr are excluded as data sanity guards.
                    </p>
                  </InfoTip>
                </p>
                <button
                  type="button"
                  onClick={handleHeadlineClick}
                  className="text-2xl font-semibold text-slate-900 text-left hover:underline focus:outline-none focus:underline cursor-pointer"
                  data-testid="closure-health-ctc-total"
                  title="Click to see the list of joined candidates"
                >
                  {formatINR(data?.candidate_ctc_total)}
                </button>
                <p className="text-xs text-slate-500 mt-0.5">
                  {data?.join_basis === 'stage_event' ? 'by Stage event' : 'by Joining date'} · in selected window
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1">
                  <UsersIcon className="w-3 h-3" /> Recruiters
                  <InfoTip id="recruiters" title="Recruiters · How it's calculated">
                    <p>
                      Number of recruiters visible to you under the role-based scope rules. TL =
                      direct reports; DM = subtree; Admin = entire org. Counts only users with the
                      <code className="bg-slate-200 px-1 rounded mx-0.5">recruiter</code> role.
                    </p>
                  </InfoTip>
                </p>
                <button
                  type="button"
                  onClick={() => isManager ? navigate('/my-team') : navigate('/command-center')}
                  className="text-2xl font-semibold text-slate-900 text-left hover:underline focus:outline-none focus:underline cursor-pointer"
                  data-testid="closure-health-recruiters"
                  title={isManager ? 'Open My Team' : 'Open Command Center'}
                >
                  {data?.recruiters_in_scope ?? 0}
                </button>
                <p className="text-xs text-slate-500 mt-0.5">in your scope</p>
              </div>
            </div>

            {/* Progress bar against monthly target */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">
                  {data?.monthly_target > 0 ? (
                    <>
                      Progress to monthly target —{' '}
                      <span className={`font-semibold ${tone.text}`}>
                        {pct}%
                      </span>
                    </>
                  ) : (
                    <span className="text-slate-500">
                      No monthly target set.{' '}
                      {isManager
                        ? 'Click "Set target" to apply an org-wide monthly goal.'
                        : 'Admins/Team Leads can set per-recruiter targets from My Team.'}
                    </span>
                  )}
                </span>
                <Badge
                  className={`${tone.bg} ${tone.text} border ${tone.border} font-medium ${
                    isManager ? 'cursor-pointer hover:opacity-80 transition' : ''
                  }`}
                  onClick={isManager ? toggleLaggards : undefined}
                  data-testid="closure-health-status"
                  title={
                    isManager
                      ? 'Click to see who is most behind'
                      : undefined
                  }
                >
                  {tone.label}
                  {isManager && (
                    <ChevronDown
                      className={`w-3 h-3 ml-1 transition-transform ${
                        laggardsOpen ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </Badge>
              </div>
              <div
                className="w-full h-2 bg-slate-200 rounded-full overflow-hidden"
                data-testid="closure-health-progress"
              >
                <div
                  className={`h-full ${tone.barClass} transition-all`}
                  style={{ width: `${pct}%` }}
                />
              </div>

              {/* Laggards drill-down (managers only) */}
              {isManager && laggardsOpen && (
                <div
                  className="mt-3 pt-3 border-t border-slate-200/70"
                  data-testid="closure-health-laggards"
                >
                  <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
                    Most behind on target
                  </p>
                  {laggardsLoading ? (
                    <div className="flex items-center gap-2 py-3 text-slate-500 text-xs">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Finding laggards…
                    </div>
                  ) : laggards.length === 0 ? (
                    <p className="text-xs text-slate-500 py-2">
                      Nobody to nudge — your team is on top of it. 🎉
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {laggards.map((lag) => {
                        const lagTone =
                          HEALTH_TONE[lag.health] || HEALTH_TONE.no_target;
                        return (
                          <li
                            key={lag.user_id}
                            className="flex items-center gap-3 bg-white/70 border border-slate-200 rounded-lg p-2"
                            data-testid={`closure-health-laggard-${lag.user_id}`}
                          >
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${lagTone.bg} ${lagTone.text} border ${lagTone.border} shrink-0`}
                            >
                              {lag.initials || '??'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p
                                className="text-sm font-medium text-slate-900 truncate"
                                title={lag.email || ''}
                              >
                                {lag.name}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {lag.closures_period} / {lag.monthly_target}{' '}
                                closures · {lag.progress_pct}% to target
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs gap-1 bg-white"
                              onClick={() =>
                                handleNudge(lag.user_id, lag.name)
                              }
                              disabled={nudgingId === lag.user_id}
                              data-testid={`closure-health-nudge-${lag.user_id}`}
                            >
                              {nudgingId === lag.user_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Send className="w-3 h-3" />
                              )}
                              Nudge
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Set-target dialog (managers only) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-md max-h-[90vh] overflow-y-auto"
          data-testid="closure-health-target-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-4 h-4 text-teal-600" />
              Set org-wide monthly closure target
            </DialogTitle>
            <DialogDescription>
              Sets the default monthly closure goal for every recruiter in your
              organisation. Per-recruiter overrides on My Team continue to win
              unless you also enable the override option below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="closure-target-input" className="text-sm font-medium">
                Monthly target (per recruiter)
              </Label>
              <Input
                id="closure-target-input"
                type="number"
                min={0}
                max={500}
                step={1}
                value={orgGoal}
                onChange={(e) => setOrgGoal(e.target.value)}
                placeholder="e.g. 5"
                data-testid="closure-target-input"
              />
              <p className="text-xs text-slate-500">
                Leave blank or set to 0 to clear the org-wide default.
              </p>
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="closure-override-personal"
                checked={overridePersonal}
                onCheckedChange={(v) => setOverridePersonal(Boolean(v))}
                data-testid="closure-override-personal"
              />
              <div className="space-y-1">
                <Label
                  htmlFor="closure-override-personal"
                  className="text-sm font-medium cursor-pointer"
                >
                  Apply to all recruiters (clear personal overrides)
                </Label>
                <p className="text-xs text-slate-500 leading-tight">
                  Wipes every recruiter's per-user goal so the new org default
                  is the single source of truth.
                </p>
              </div>
            </div>

            {/* Auto-nudge weekly job — fully self-contained switch row */}
            <div
              className="flex items-start justify-between gap-3 pt-3 border-t border-slate-200"
              data-testid="closure-auto-nudge-row"
            >
              <div className="space-y-1 flex-1">
                <Label
                  htmlFor="closure-auto-nudge-switch"
                  className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5 text-teal-600" />
                  Auto-nudge on Friday at 5 PM
                </Label>
                <p className="text-xs text-slate-500 leading-tight">
                  Every {autoNudge.schedule_label.replace('Every ', '')}, the
                  system pings any recruiter below {autoNudge.threshold_pct}%
                  of their monthly target — in-app + Slack channel (if
                  connected). Hands-off accountability.
                </p>
              </div>
              <Switch
                id="closure-auto-nudge-switch"
                checked={autoNudge.enabled}
                disabled={autoNudgeSaving}
                onCheckedChange={handleToggleAutoNudge}
                data-testid="closure-auto-nudge-switch"
              />
            </div>

            {/* Closure of the week — Monday celebration */}
            <div
              className="flex items-start justify-between gap-3 pt-3 border-t border-slate-200"
              data-testid="closure-of-week-row"
            >
              <div className="space-y-1 flex-1">
                <Label
                  htmlFor="closure-of-week-switch"
                  className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                >
                  <Trophy className="w-3.5 h-3.5 text-amber-500" />
                  Closure of the week — Monday 9 AM
                </Label>
                <p className="text-xs text-slate-500 leading-tight">
                  Every {closureOfWeek.schedule_label.replace('Every ', '')}, the
                  system posts a celebration in your Slack channel highlighting
                  last week's top recruiter — closures + Candidate CTC closed.
                  Pairs perfectly with the Friday auto-nudge.
                </p>
              </div>
              <Switch
                id="closure-of-week-switch"
                checked={closureOfWeek.enabled}
                disabled={closureOfWeekSaving}
                onCheckedChange={handleToggleClosureOfWeek}
                data-testid="closure-of-week-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={savingTarget}
              data-testid="closure-target-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveTarget}
              disabled={savingTarget}
              data-testid="closure-target-save"
            >
              {savingTarget ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                'Save target'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Joined Candidates drill-down — shows the actual rows that make up the
          headline "21 closures · ₹1.12 Cr" so admins can verify the numbers
          rather than just see an aggregate. Same visibility scope as the
          parent endpoint (admin = org, DM = subtree, TL = directs, recruiter = own). */}
      <Dialog open={joinedOpen} onOpenChange={setJoinedOpen}>
        <DialogContent
          className="max-w-4xl"
          data-testid="closure-health-joined-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-emerald-600" />
              Joined candidates · {data?.period_label || '—'}
            </DialogTitle>
            <DialogDescription>
              {joinedLoading
                ? 'Loading…'
                : (
                  <span>
                    <span className="font-semibold text-slate-800">{joinedTotal}</span>{' '}
                    candidate{joinedTotal === 1 ? '' : 's'} joined ·{' '}
                    <span className="font-semibold text-slate-800">
                      {formatINR(joinedCtcTotal)}
                    </span>{' '}
                    total CTC
                  </span>
                )}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
            {joinedLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading joined candidates…
              </div>
            ) : joinedItems.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm" data-testid="joined-dialog-empty">
                No candidates joined in this window. Switch the period above to expand the range.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="joined-dialog-list">
                {joinedItems.map((it) => (
                  <li
                    key={it.application_id}
                    className="py-2.5 px-1 flex items-start gap-3 hover:bg-slate-50 rounded transition-colors group"
                    data-testid={`joined-row-${it.application_id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                      {(it.candidate_name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {it.candidate_name}
                        </span>
                        <span className="text-[11px] text-slate-500 inline-flex items-center gap-0.5">
                          <Briefcase className="w-3 h-3" /> {it.job_title}
                        </span>
                        <span className="text-[11px] text-slate-500 inline-flex items-center gap-0.5">
                          <Building2 className="w-3 h-3" /> {it.client_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-600 flex-wrap">
                        {it.joining_date && (
                          <span className="inline-flex items-center gap-0.5">
                            <CalendarIcon className="w-3 h-3" />
                            Joined {it.joining_date}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <UserAvatarInline name={it.recruiter_name} />
                          <span className="text-slate-500">{it.recruiter_name}</span>
                        </span>
                        {it.ctc_source === 'fallback' && (
                          <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-amber-300 text-amber-700">
                            from candidate
                          </Badge>
                        )}
                        {it.ctc_source === 'excluded' && (
                          <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-rose-300 text-rose-700">
                            CTC out of range
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-slate-900 font-mono">
                        {formatINR(it.offered_ctc)}
                      </div>
                      {it.candidate_id && (
                        <button
                          type="button"
                          onClick={() => {
                            setJoinedOpen(false);
                            navigate(`/candidates/${it.candidate_id}`);
                          }}
                          className="text-[10px] text-teal-700 hover:underline inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`joined-row-open-${it.application_id}`}
                        >
                          Open <ExternalLink className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter className="flex items-center justify-between flex-wrap gap-2 sm:flex-row">
            <p className="text-[11px] text-slate-500">
              Scope: {isManager ? 'visible reportee tree' : 'your closures only'} · period switches in real-time
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setJoinedOpen(false)}
              data-testid="joined-dialog-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ClosureHealthReport;
